import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { Reveal } from "../lib/motion";
import { CONTACT, NAV } from "../data";

export const Contact = () => (
  <section
    id="contact"
    className="relative overflow-hidden border-t border-black/10 pt-28 md:pt-36"
    data-testid="contact-section"
  >
    {/* ambient glow */}
    <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-[var(--accent)]/[0.10] blur-[140px]" />

    <div className="container-vx relative">
      <Reveal>
        <span className="label-mono">/ Let's build</span>
      </Reveal>

      <Reveal delay={0.1}>
        <h2 className="mt-6 max-w-5xl font-sans text-[13vw] font-light leading-[0.9] tracking-tighter sm:text-7xl md:text-8xl lg:text-[8rem]">
          Make your operations
          <br />
          <span className="italic text-neutral-400">autonomous.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.2} className="mt-12">
        <a
          href={`mailto:${CONTACT.email}`}
          data-testid="contact-cta"
          className="group inline-flex items-center gap-3 rounded-full bg-black px-8 py-4 font-body text-lg font-medium text-white transition-transform duration-300 hover:scale-[1.03]"
        >
          Book a Strategy Call
          <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </Reveal>

      {/* Contact cards */}
      <div className="mt-20 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
        {[
          { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
          { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`, testid: "contact-phone" },
          { icon: MapPin, label: "Head Office", value: CONTACT.address, href: "https://maps.google.com/?q=30+Eglinton+Ave+W+Mississauga+ON+L5R+3E7", testid: "contact-address" },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <Reveal key={c.label} delay={i * 0.08}>
              <a
                href={c.href}
                target={c.icon === MapPin ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={c.testid}
                className="group flex h-full flex-col justify-between rounded-2xl border border-black/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-black/25 hover:card-shadow"
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 text-[var(--accent)]" strokeWidth={1.5} />
                  <span className="label-mono">{c.label}</span>
                </div>
                <span className="mt-10 font-sans text-lg font-medium leading-snug tracking-tight text-neutral-900 transition-colors group-hover:text-black">
                  {c.value}
                </span>
              </a>
            </Reveal>
          );
        })}
      </div>
    </div>

    {/* Footer */}
    <footer className="container-vx relative mt-28 border-t border-black/10 py-12">
      <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
        <a href="#top" className="flex items-center" aria-label="Vertex Automation home">
          <img
            src="/vertex-logo.png"
            alt="Vertex Automation"
            className="h-16 w-auto object-contain md:h-20"
          />
        </a>

        <nav className="flex flex-wrap gap-x-8 gap-y-2">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="font-body text-sm text-neutral-500 transition-colors hover:text-black"
            >
              {n.label}
            </a>
          ))}
          <a href="#faq" className="font-body text-sm text-neutral-500 transition-colors hover:text-black">
            FAQ
          </a>
        </nav>
      </div>

      <div className="mt-8 flex flex-col justify-between gap-2 border-t border-black/5 pt-8 md:flex-row">
        <p className="font-mono text-xs text-neutral-400">
          © 2023 Vertex Automation Canada · All rights reserved.
        </p>
        <p className="font-mono text-xs text-neutral-400">
          Intelligent automation, engineered.
        </p>
      </div>
    </footer>

    {/* Giant background wordmark */}
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.2 }}
      className="pointer-events-none select-none overflow-hidden"
      aria-hidden="true"
    >
      <div className="whitespace-nowrap text-center font-sans text-[19vw] font-light leading-none tracking-tighter text-transparent [-webkit-text-stroke:1px_rgba(0,0,0,0.07)]">
        VERTEX
      </div>
    </motion.div>
  </section>
);
